import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { useAuthStore } from '@/stores/auth';

/**
 * Routing and the authentication guard.
 *
 * `meta.public` marks the two screens reachable without a session; `meta.guestOnly`
 * marks the ones a signed-in user should be bounced away from. The guard awaits
 * `auth.bootstrap()` on the first navigation so a page reload does not flash the
 * login screen before the persisted session has been rehydrated and verified.
 */

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean;
    guestOnly?: boolean;
    title?: string;
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/projects' },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, guestOnly: true, title: 'Đăng nhập' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { public: true, guestOnly: true, title: 'Đăng ký' },
  },
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: { title: 'Project' },
  },
  {
    path: '/agents',
    name: 'agents',
    component: () => import('@/views/AgentsView.vue'),
    meta: { title: 'Agent & trình duyệt' },
  },
  {
    path: '/posts',
    name: 'posts',
    component: () => import('@/views/PostsView.vue'),
    meta: { title: 'Bài viết' },
  },
  {
    path: '/comment-jobs',
    name: 'comment-jobs',
    component: () => import('@/views/CommentJobsView.vue'),
    meta: { title: 'Bình luận' },
  },
  {
    path: '/selector-packs',
    name: 'selector-packs',
    component: () => import('@/views/SelectorPacksView.vue'),
    meta: { title: 'SelectorPack' },
  },
  {
    path: '/ai-prompts',
    name: 'ai-prompts',
    component: () => import('@/views/AiPromptsView.vue'),
    meta: { title: 'Prompt AI' },
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { title: 'Cài đặt' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true, title: 'Không tìm thấy' },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  await auth.bootstrap();

  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: 'projects' };
  }

  return true;
});

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · MidiAutoListening` : 'MidiAutoListening';
});

export default router;
